import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "../utils/seo";
import { ProjectContinuity } from "../components/ProjectContinuity";
import { CopyDetails } from "../components/CopyDetails";

const About = () => {
  useSEO({
    title: "About | Onward Land Solutions",
    description:
      "Onward Land Solutions was built from real land project experience. We bring structure, visibility, and discipline to the land side of complex infrastructure, energy, utility, renewable, and development projects.",
    canonicalPath: "/about",
  });

  return (
    <>
      <section style={{ background: "linear-gradient(180deg, #090A0C 0%, #15171B 100%)" }} className="pt-24 pb-16 relative overflow-hidden">
        <div className="ols-parcel-grid absolute inset-0" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="ols-eyebrow ols-eyebrow--light mb-4">About Onward Land Solutions</p>
          <h1 className="ols-h1">Land experience. Project discipline.</h1>
        </div>
      </section>

      <section className="ols-section" style={{ background: "#fff" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <p className="ols-lede">
            Onward brings practical land experience to energy and infrastructure projects: ROW, title, negotiations, GIS and survey coordination.
          </p>
          <p className="ols-lede">
            Experienced people lead. Internal technology supports the work.
          </p>
        </div>
      </section>

      <section className="ols-section" style={{ background: "var(--ols-limestone)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-8">
          <div>
            <p className="ols-eyebrow mb-3">Experience</p>
            <h3 className="ols-h3 mb-3" style={{ color: "var(--ols-navy)" }}>Practical land knowledge.</h3>
            <p className="text-[15px] leading-relaxed" style={{ color: "var(--ols-body)" }}>
              Land and access experience across energy and infrastructure.
            </p>
          </div>
          <div>
            <p className="ols-eyebrow mb-3">Systems</p>
            <h3 className="ols-h3 mb-3" style={{ color: "var(--ols-navy)" }}>Structured workflows.</h3>
            <p className="text-[15px] leading-relaxed" style={{ color: "var(--ols-body)" }}>
              GIS and scoped reporting, guided by professional judgment.
            </p>
          </div>
          <div>
            <p className="ols-eyebrow mb-3">Discipline</p>
            <h3 className="ols-h3 mb-3" style={{ color: "var(--ols-navy)" }}>Project pressure respected.</h3>
            <p className="text-[15px] leading-relaxed" style={{ color: "var(--ols-body)" }}>
              Clear responsibilities, dependencies and follow-through.
            </p>
          </div>
        </div>
      </section>

      <section className="ols-section" style={{ background: "#15171B", color: "#F5F2EC" }} data-testid="about-continuity-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8"><ProjectContinuity id="about" />
          <p className="mt-6 text-sm leading-relaxed" data-testid="about-pathscore-sequence">PathScore connects corridor review to scoped title and execution planning.</p>
          <Link to="/path-score?source=about_continuity" className="inline-flex underline mt-4 text-sm" data-testid="about-pathscore-link">Explore PathScore</Link>
        </div>
      </section>

      {/* Compliance note */}
      <section className="ols-section" style={{ background: "#fff" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <CopyDetails id="about-scope" title="Land services and survey coordination" tone="light">
          <p className="text-[15px] leading-relaxed mb-4" style={{ color: "var(--ols-body)" }}>
            Onward manages land, acquisition, title support, corridor intelligence, GIS tracking and field-access coordination.
          </p>
          </CopyDetails>
          <p className="text-[15px] leading-relaxed" style={{ color: "var(--ols-body)" }}>
            Onward does <strong>not</strong> provide licensed surveying services. Survey work is performed by
            properly licensed survey professionals. Onward coordinates the land, access, communication, and
            project workflow around that work.
          </p>
        </div>
      </section>

      <section style={{ background: "linear-gradient(135deg, #090A0C 0%, #15171B 100%)" }} className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="ols-h2 mb-8" style={{ color: "#fff" }}>Tell us what comes next.</h2>
          <Link to="/contact?source=about_footer_cta" className="ols-btn-primary" data-testid="about-project-cta">Talk to Onward <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </>
  );
};

export default About;
